import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  getTranslations,
  exerciseAsyncActions,
  getExerciseToDo,
  getExerciseDone,
  getExerciseStatistics
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {TranslationKeys} from 'utils/constants/routeConstants';

import {ReactComponent as XpeditionIcon} from 'assets/image/menu-auth/DISCOVERY_GABI1.svg';
import NotFoundInfo from '../../components/not-found-info';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {clearExerciseDialogue} from '../../redux/exercise/actions';
import {IExerciseDialogue} from '../../redux/exercise/interfaces';
import ExerciseCardSection from '../../components/exercise-card-section';
import {ExerciseCollapse} from './exercise-collapse/ExerciseCollapse';
import {ExerciseStats} from './exercise-stats/ExerciseStats';

import './styles.scss';

export const Exercise: React.FunctionComponent = () => {
  const {
    exerciseToDo,
    exerciseDone
  } = useSelector((state: IStore) => state.exercise);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(clearExerciseDialogue());
  }, []);

  useGetTranslations({
    translationKey: TranslationKeys.Exercise
  });

  useEffect(() => {
    dispatch(exerciseAsyncActions[getExerciseToDo]());
    dispatch(exerciseAsyncActions[getExerciseDone]());
    dispatch(exerciseAsyncActions[getExerciseStatistics]());
  }, []);

  function handleOpenDialogue(dialogue: IExerciseDialogue) {
    navigate(`/exercise/${dialogue.dialogId}`);
  }

  function renderToDoDialogueCards() {
    return <div className="exercise__cards">
      {exerciseToDo.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.exercise.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
      /> : <ExerciseCardSection
        showLoader={loading.includes(getExerciseToDo)}
        cards={exerciseToDo}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  function renderDoneDialogueCards() {
    return <div className="exercise__cards">
      {exerciseDone.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.exercise.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
      /> : <ExerciseCardSection
        showLoader={loading.includes(getExerciseDone)}
        cards={exerciseDone}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'exercise container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="exercise__title">
          <XpeditionIcon className="exercise__icon"/>
          <FormattedMessage
            id={'gritx.exercise.title'}
            defaultMessage={'Exercise'}
          />
        </h1>
        <ExerciseCollapse collapsed={true} title={intl.formatMessage({
          id: 'gritx.exercise.stats.title',
          defaultMessage: 'Statistics'
        })}>
          <ExerciseStats/>
        </ExerciseCollapse>
        <ExerciseCollapse collapsed={false} title={intl.formatMessage({
          id: 'gritx.exercise.todo.title',
          defaultMessage: 'To do'
        })}>
          <>
            {
              loading.includes(getExerciseToDo) ? <Loader/> : renderToDoDialogueCards()
            }
          </>
        </ExerciseCollapse>
        <ExerciseCollapse collapsed={true} title={intl.formatMessage({
          id: 'gritx.exercise.done.title',
          defaultMessage: 'Done'
        })}>
          <>
            {
              loading.includes(getExerciseDone) ? <Loader/> : renderDoneDialogueCards()
            }
          </>
        </ExerciseCollapse>
      </>
    }
  </section>;
};
